import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTeams } from '../api/endpoints/useTeams';
import type { Team } from '../types/team';
import { useClientId } from './useClientId';

export interface UseTeamSelectionResult {
  selectedTeams: Team[];
  allTeams: Team[];
  isLoading: boolean;
  error: unknown;
  updateSelectedTeams: (teams: Team[]) => void;
}

export function useTeamSelection(): UseTeamSelectionResult {
  const clientId = useClientId();
  const { data: teams = [], isLoading, error } = useTeams(clientId);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTeams: Team[] = useMemo(() => {
    const teamIdsParam = searchParams.get('teams');
    if (teamIdsParam) {
      const teamIds = teamIdsParam.split(',');
      return teams.filter(team => teamIds.includes(team.id));
    }
    // No URL params means all teams are selected
    return teams;
  }, [searchParams, teams]);

  const updateSelectedTeams = useCallback(
    (teamsToSelect: Team[]) => {
      const currentParam = searchParams.get('teams');

      // Normalize helper for deterministic comparison
      const normalizeIds = (ids: string | null) =>
        ids
          ? ids
              .split(',')
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b))
              .join(',')
          : '';
      const nextIds =
        teamsToSelect.length === 0 || teamsToSelect.length === teams.length
          ? ''
          : teamsToSelect
              .map(t => t.id)
              .sort((a, b) => a.localeCompare(b))
              .join(',');
      const currentIds = normalizeIds(currentParam);

      // No-op if nothing effectively changes
      if (currentIds === nextIds) {
        return;
      }

      const newParams = new URLSearchParams(searchParams);
      if (nextIds === '') {
        // Remove the param for all teams (default)
        newParams.delete('teams');
      } else {
        newParams.set('teams', nextIds);
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, teams.length]
  );

  return {
    selectedTeams,
    allTeams: teams,
    isLoading,
    error,
    updateSelectedTeams,
  };
}
